import { Box, Button, Typography } from '@mui/material';
import templatePreviewPlaceholder from 'src/assets/images/templatePreviewPlaceholder.svg';
import { Breadcrumbs } from 'src/components/breadcrumbs';
import { ProductListModal } from 'src/components/modal';
import { PATHS } from 'src/constants/paths';
import { StyledDiscardButton } from 'src/features/products/addProductPage/AddProductPage.styled';
import { IDyTemplate, useGetCCAssets } from 'src/hooks';
import { IProduct } from 'src/interfaces';
import { lang } from 'src/utils';

import { ButtonContainer } from '../../AddVariationTemplatePage.styled';
import { VariationTemplateItem } from '../variationTemplateItem/VariationTemplateItem';
import { TemplatePreviewSection } from './TemplatePreviewSection';
import { useVariationTemplateMappingStep } from './useVariationTemplateMappingStep.hook';
import {
  HeaderContainer,
  SideMenuHeader,
  VariationTemplateContainer,
  VariationTemplateContentContainer,
  VariationTemplatePreviewContainer,
  VariationTemplatePreviewPlaceholder,
  VariationTemplatePreviewWrapper,
  VariationTemplateSideMenuContainer,
} from './VariationTemplateMappingStep.styled';
import { type VariationMap } from './VariationTemplateMappingStep.utils';

interface VariationTemplateMappingStepProps {
  template: IDyTemplate;
  variationMap: VariationMap;
  setVariationMap: React.Dispatch<React.SetStateAction<VariationMap>>;
  onNext: () => Promise<void>;
  onDiscard: () => void;
  isSaving?: boolean;
  mainProductAssetName?: string;
  isVariationMapDirty?: boolean;
}

export const VariationTemplateMappingStep = ({
  template,
  variationMap,
  setVariationMap,
  onDiscard,
  onNext,
  isVariationMapDirty = true,
  isSaving = false,
}: VariationTemplateMappingStepProps) => {
  const { data: assets } = useGetCCAssets();
  const {
    productListModal,
    handleSelectProduct,
    handleVariationChange,
    handleProductCodeChange,
    handleShowFlavorsChange,
    filters,
    showPreviewPlaceholder,
    isSingleProduct,
  } = useVariationTemplateMappingStep({
    variationMap,
    setVariationMap,
    template,
  });

  return (
    <VariationTemplateContainer>
      <VariationTemplateSideMenuContainer>
        <SideMenuHeader>
          <Typography variant="h6" fontSize={'20px'}>
            Template {template?.name?.replace('_', ' ')}
          </Typography>
        </SideMenuHeader>
        {Object.entries(variationMap).map(([key, variation]) => (
          <VariationTemplateItem
            key={key}
            index={Number(key)}
            productCode={variation.productCode}
            variationCode={variation.variationCode}
            options={{
              showRadioGroup: Number(key) !== 1,
              showActionButton: Number(key) !== 1,
              showToggle: isSingleProduct,
            }}
            showFlavors={variation.showFlavors}
            onSelectProduct={() => {
              productListModal.onOpenModal((products: IProduct[]) =>
                handleSelectProduct(products, Number(key)),
              );
            }}
            onVariationChange={(value: string) =>
              handleVariationChange(Number(key), value)
            }
            onProductCodeChange={(value: string) =>
              handleProductCodeChange(Number(key), value)
            }
            onShowFlavorsChange={(value: boolean) =>
              handleShowFlavorsChange(Number(key), value)
            }
            assets={assets}
          />
        ))}
      </VariationTemplateSideMenuContainer>
      <VariationTemplateContentContainer>
        <HeaderContainer>
          <Breadcrumbs
            items={[
              {
                label: lang.products,
                link: PATHS.PRODUCTS,
              },
              {
                label: lang.edit,
              },
            ]}
          />
          <Typography variant="h3" lineHeight="36px">
            {lang.dyDisplay}
          </Typography>
        </HeaderContainer>

        <VariationTemplatePreviewContainer>
          {showPreviewPlaceholder ? (
            <VariationTemplatePreviewWrapper>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={2}
                textAlign="center">
                <VariationTemplatePreviewPlaceholder
                  src={templatePreviewPlaceholder}
                  alt="Template preview placeholder"
                />
              </Box>
            </VariationTemplatePreviewWrapper>
          ) : (
            <TemplatePreviewSection
              template={template}
              variationMap={variationMap}
              assets={assets}
            />
          )}
        </VariationTemplatePreviewContainer>

        <ButtonContainer>
          <Box style={{ flexGrow: 1 }} />
          <StyledDiscardButton variant="text" onClick={onDiscard}>
            {lang.discard}
          </StyledDiscardButton>
          <Button
            variant="contained"
            disabled={
              !isVariationMapDirty ||
              isSaving ||
              Object.values(variationMap).some(
                (variation) =>
                  !variation.productCode || !variation.variationCode,
              )
            }
            onClick={() => onNext()}>
            {lang.saveDyDisplay}
          </Button>
        </ButtonContainer>
      </VariationTemplateContentContainer>

      {productListModal.open && (
        <ProductListModal
          open={productListModal.open}
          onClose={productListModal.onCloseModal}
          onSelect={productListModal.customFn}
          heading={lang.addProducts}
          hasExactSearch={true}
          filters={filters}
          singleSelectionMode
        />
      )}
    </VariationTemplateContainer>
  );
};
